import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronRight, Inbox } from 'lucide-react';
import { useGuidedOrders } from '@/hooks/useGuidedOrders';
import { GuidedOrderStatusBadge } from '@/components/guided-order/GuidedOrderStatusBadge';
import { canPrescribeAgainstGuidedOrder } from '@/lib/guidedOrderEligibility';
import type { GuidedOrder } from '@/types/guidedOrder';

export interface GuidedOrderPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientClinicId: string;
  myPractitionerId: string;
  onSelect: (orderId: string) => void;
  onViewAll?: () => void;
}

function formatDate(ts: string | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString();
}

export default function GuidedOrderPickerDialog({
  open,
  onOpenChange,
  patientId,
  patientClinicId,
  myPractitionerId,
  onSelect,
  onViewAll,
}: GuidedOrderPickerDialogProps) {
  const { data, isLoading, error } = useGuidedOrders({
    clinic_id: patientClinicId,
    patient_id: patientId,
    fulfillment_source: 'nimbus',
    status: 'mdi_completed',
    limit: 50,
  });

  const eligibleOrders = useMemo<GuidedOrder[]>(() => {
    const list = data?.data || [];
    return list.filter(o => canPrescribeAgainstGuidedOrder(o, myPractitionerId));
  }, [data, myPractitionerId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Select a guided order</DialogTitle>
          <DialogDescription>
            Pick the guided order you're prescribing for. Only orders awaiting your prescription are shown.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="my-2 max-h-[60vh]">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          )}

          {error && !isLoading && (
            <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">
              Failed to load guided orders. Please try again.
            </div>
          )}

          {!isLoading && !error && eligibleOrders.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Inbox className="h-6 w-6 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-700">No guided orders ready to prescribe</p>
              <p className="mt-1 text-xs text-slate-500">
                This patient has no Nimbus guided orders awaiting your prescription.
              </p>
              {onViewAll && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => {
                    onOpenChange(false);
                    onViewAll();
                  }}
                >
                  View Guided Orders
                </Button>
              )}
            </div>
          )}

          {!isLoading && !error && eligibleOrders.length > 0 && (
            <ul className="space-y-2">
              {eligibleOrders.map(order => {
                const programLabel =
                  order.clinicProgramOption?.label ||
                  ((order.metadata as Record<string, unknown>)?.program_label as string | undefined) ||
                  '—';
                return (
                  <li key={order.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(order.id)}
                      className="group flex w-full items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-3 text-left transition-colors hover:border-teal-300 hover:bg-teal-50/50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 font-mono text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-200">
                            {order.order_number != null ? `GO-${order.order_number}` : order.id.slice(0, 8)}
                          </span>
                          <GuidedOrderStatusBadge status={order.status} />
                        </div>
                        <p className="mt-1.5 truncate text-sm font-medium text-slate-900">
                          {programLabel}
                        </p>
                        <p className="text-xs text-slate-500">
                          Created {formatDate(order.created_at)}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-teal-600" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
